import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Flex, Text, Palette } from '@updater/ui-uds';
import { InitiatePayload } from 'flows/core/types/cards';
import { useProgressTotals } from '../../../hooks/useProgressTotals';
import { ChatColors } from '../../../configs/chat-colors';

// ===== TYPES =====
export interface CompactProgressBarProps {
  priorityCards: InitiatePayload[];
  completedCards: InitiatePayload[];
  onAnimationComplete?: () => void;
  shouldAnimate?: boolean; // Control whether to animate or show static
  showTasksButton?: boolean;
  onTasksButtonClick?: () => void;
}

type AnimationPhase = 'initial' | 'white' | 'blue';

interface AnimationLayerProps {
  animationPhase: AnimationPhase;
}

interface ProgressBarFillProps extends AnimationLayerProps {
  widthPct: number;
}

// ===== CONSTANTS =====
const ANIMATION_TIMING = {
  INITIAL_DELAY: 100,
  COLOR_TRANSITION_DELAY: 800,
  WIDTH_DURATION: '1.2s',
  WHITE_FADE_DURATION: '0.3s',
  BLUE_FADE_DURATION: '1s',
} as const;

const ANIMATION_EASING = {
  WIDTH: 'cubic-bezier(0.2, 0, 0.1, 1)',
  WHITE_FADE: 'cubic-bezier(0.2, 0, 0.05, 1)',
  BLUE_FADE: 'cubic-bezier(0.2, 0, 0.05, 1)',
} as const;

const GRADIENTS = {
  WHITE: ChatColors.progress.GRADIENT_WHITE,
  BLUE: ChatColors.progress.GRADIENT_BLUE,
} as const;

// ===== STYLED COMPONENTS =====
const TasksButton = styled.button`
  background: ${ChatColors.progress.BUTTON_BG};
  border: none;
  color: ${ChatColors.progress.BUTTON_COLOR};
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 400;
  padding: 0.35rem 0.8rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  display: flex;
  align-items: center;
  &:hover,
  &:focus {
    background: ${ChatColors.progress.BUTTON_HOVER_BG};
    color: ${ChatColors.progress.BUTTON_HOVER_COLOR};
  }
  &:active {
    background: ${ChatColors.progress.BUTTON_HOVER_BG};
    color: ${ChatColors.progress.BUTTON_HOVER_COLOR};
  }
`;
const Container = styled(Flex)`
  width: 100%;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
`;

const MiniBarTrack = styled(Box)`
  flex: 1;
  height: 6px;
  background-color: ${ChatColors.progress.TRACK_BG};
  border-radius: 9999px;
  overflow: hidden;
`;

const MiniBarFill = styled(Box)<ProgressBarFillProps>`
  width: ${({ widthPct, animationPhase }) => {
    if (animationPhase === 'initial') return '0%';
    return `${Math.min(100, Math.max(0, widthPct))}%`;
  }};
  height: 100%;
  position: relative;
  transition: width ${ANIMATION_TIMING.WIDTH_DURATION} ${ANIMATION_EASING.WIDTH};
  transform-origin: left;
`;

const BlueLayer = styled(Box)<AnimationLayerProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${GRADIENTS.BLUE};
  opacity: ${({ animationPhase }) => (animationPhase === 'blue' ? 1 : 0)};
  transition: opacity ${ANIMATION_TIMING.BLUE_FADE_DURATION}
    ${ANIMATION_EASING.BLUE_FADE};
`;

// ===== CUSTOM HOOKS =====
const useAnimationSequence = (
  targetPct: number,
  shouldAnimate: boolean,
  onAnimationComplete?: () => void
) => {
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>(
    shouldAnimate ? 'initial' : 'blue'
  );
  const [prevPct, setPrevPct] = useState(-1);
  const [hasStartedInitialAnimation, setHasStartedInitialAnimation] = useState(
    !shouldAnimate
  );

  const resetAnimation = useCallback(() => {
    setAnimationPhase('initial');
  }, []);

  const triggerBluePhase = useCallback(() => {
    setAnimationPhase('blue');
    setPrevPct(targetPct);
  }, [targetPct]);

  const triggerAnimationComplete = useCallback(() => {
    onAnimationComplete?.();
    setHasStartedInitialAnimation(true);
  }, [onAnimationComplete]);

  useEffect(() => {
    // Animate in these cases:
    // 1. Initial animation: shouldAnimate is true AND we haven't started initial animation yet
    // 2. Value change animation: percentage actually changed (regardless of shouldAnimate)
    const shouldTriggerAnimation =
      (shouldAnimate && !hasStartedInitialAnimation) || // Initial animation
      (targetPct !== prevPct && prevPct !== -1); // Value change (but not first value)

    if (shouldTriggerAnimation) {
      resetAnimation();

      const timer1 = setTimeout(
        triggerBluePhase,
        ANIMATION_TIMING.INITIAL_DELAY
      );

      // Only call completion callback for the initial animation
      let timer2: NodeJS.Timeout | null = null;
      if (shouldAnimate && !hasStartedInitialAnimation) {
        const totalDuration = ANIMATION_TIMING.INITIAL_DELAY + 1000;
        timer2 = setTimeout(triggerAnimationComplete, totalDuration);
      } else {
        // For value changes, just update prevPct after animation
        timer2 = setTimeout(
          () => setPrevPct(targetPct),
          ANIMATION_TIMING.INITIAL_DELAY + 1000
        );
      }

      return () => {
        clearTimeout(timer1);
        if (timer2) clearTimeout(timer2);
      };
    }
    return undefined;
  }, [
    targetPct,
    prevPct,
    shouldAnimate,
    hasStartedInitialAnimation,
    resetAnimation,
    triggerBluePhase,
    triggerAnimationComplete,
  ]);

  return animationPhase;
};
// ===== HELPER COMPONENTS =====
interface ProgressDisplayProps {
  completed: number;
  total: number;
}

const TasksDisplay: React.FC<
  ProgressDisplayProps & { asButton?: boolean; onClick?: () => void }
> = ({ completed, total, asButton, onClick }) => {
  const label = completed === 0 ? 'Start now' : `Tasks ${completed}/${total}`;
  if (asButton) {
    return (
      <TasksButton type="button" onClick={onClick} aria-label={label}>
        <span>{label}</span>
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.773 9.39795L7.14797 15.023C7.0693 15.1017 6.96903 15.1554 6.85986 15.1771C6.75069 15.1988 6.63752 15.1877 6.53468 15.1451C6.43184 15.1025 6.34396 15.0303 6.28216 14.9377C6.22036 14.8451 6.18741 14.7363 6.1875 14.625V3.37499C6.18741 3.26367 6.22036 3.15483 6.28216 3.06225C6.34396 2.96966 6.43184 2.8975 6.53468 2.85489C6.63752 2.81227 6.75069 2.80113 6.85986 2.82288C6.96903 2.84462 7.0693 2.89826 7.14797 2.97702L12.773 8.60202C12.8253 8.65426 12.8668 8.7163 12.8951 8.78458C12.9234 8.85287 12.9379 8.92606 12.9379 8.99999C12.9379 9.07391 12.9234 9.1471 12.8951 9.21539C12.8668 9.28368 12.8253 9.34571 12.773 9.39795Z"
              fill="currentColor"
            />
          </svg>
        </span>
      </TasksButton>
    );
  }
  return (
    <Text variant="xsBold" color={Palette.white} as="span">
      {label}
    </Text>
  );
};

interface AnimatedBarProps {
  percentage: number;
  animationPhase: AnimationPhase;
}

const AnimatedBar: React.FC<AnimatedBarProps> = ({
  percentage,
  animationPhase,
}) => (
  <MiniBarTrack aria-hidden>
    <MiniBarFill widthPct={percentage} animationPhase={animationPhase}>
      <BlueLayer animationPhase={animationPhase} />
    </MiniBarFill>
  </MiniBarTrack>
);

// ===== MAIN COMPONENT =====
export const CompactProgressBar: React.FC<CompactProgressBarProps> = ({
  priorityCards,
  completedCards,
  onAnimationComplete,
  shouldAnimate = true, // Default to true for backward compatibility,
  showTasksButton,
  onTasksButtonClick,
}) => {
  // Data hooks
  const {
    pct: percentage,
    completedTasks,
    totalTasks,
  } = useProgressTotals(priorityCards, completedCards);

  // Animation hook
  const animationPhase = useAnimationSequence(
    percentage,
    shouldAnimate,
    onAnimationComplete
  );

  // Memoized components for performance
  const tasksDisplay = useMemo(
    () => (
      <TasksDisplay
        completed={completedTasks}
        total={totalTasks}
        asButton={!!showTasksButton}
        onClick={onTasksButtonClick}
      />
    ),
    [completedTasks, totalTasks, showTasksButton, onTasksButtonClick]
  );

  const animatedBar = useMemo(
    () => (
      <AnimatedBar percentage={percentage} animationPhase={animationPhase} />
    ),
    [percentage, animationPhase]
  );

  return (
    <Container
      role="region"
      aria-label="Move progress"
      onClick={onTasksButtonClick}
    >
      {tasksDisplay}
      {animatedBar}
      {/* {daysDisplay} */}
    </Container>
  );
};

export default CompactProgressBar;
